import React from 'react';
import { 
  Typography, 
  Card, 
  CardContent, 
  CardMedia, 
  Box,
  Chip,
  Divider
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import { logAnalyticsEvent } from '../../utils/analytics';
import { color, radius, r } from '../../theme/tokens';
import './BlogPost.css';

/**
 * Reusable blog card component
 * @param {Object} post - The blog post object
 */
function BlogCard({ post }) {
  const navigate = useNavigate();

  const handleCardClick = () => {
    // Track blog card click
    logAnalyticsEvent('click_blog_card', {
      blog_slug: post.slug,
      blog_title: post.title,
      blog_author: post.author,
      blog_categories: post.categories.join(', '),
      blog_read_time: post.readTime,
      click_location: 'blog_listing_page'
    });
    
    navigate(`/blog/${post.slug}`);
  };

  return (
    <Card 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        borderRadius: radius.md,
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        border: `${r(1)} solid rgba(0,0,0,0.05)`,
        overflow: 'hidden',
        '&:hover': {
          transform: `translateY(${r(-6)})`,
          boxShadow: `0 ${r(8)} ${r(20)} rgba(0,0,0,0.08)`,
          cursor: 'pointer'
        }
      }}
      onClick={handleCardClick}
    >
      <CardMedia
        component="img"
        height="200"
        image={post.image}
        alt={post.title}
        sx={{ objectFit: 'cover' }}
      />
      <CardContent sx={{ flexGrow: 1, p: 3 }}>
        <Typography 
          gutterBottom 
          variant="h5" 
          component="h2"
          sx={{ 
            fontWeight: 600,
            fontSize: '1.25rem',
            mb: 1,
            color: color.evergreen
          }}
        >
          {post.title}
        </Typography>
        <Typography 
          variant="body2" 
          color="text.secondary"
          sx={{ mb: 2 }}
        >
          {post.excerpt}
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography 
            variant="caption" 
            color="text.secondary"
          >
            {post.date} • {post.readTime}
          </Typography>
          {post.author && post.author.trim() !== "" && (
            <Typography 
              variant="caption" 
              color="text.secondary"
            >
              By {post.author}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {post.categories.map((category, index) => (
            <Chip 
              key={index} 
              label={category} 
              size="small"
              sx={{ 
                bgcolor: alpha(color.accent, 0.1),
                color: color.evergreen,
                fontWeight: 500
              }} 
            />
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}

export default BlogCard; 